import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag } from 'react-dnd';
import { updateComponent, selectComponent, updateComponentPosition, selectComponents } from '../redux/editorSlice';
import { COMPONENT_TYPES } from '../constants';
import ReviewFormComponent from './common/ReviewFormComponent';

// 모듈화된 컴포넌트 시스템 연동 임포트
import {
  getModularizedComponent,
  getModularizedEditorComponent
} from './module';

// 컴포넌트 어댑터 유틸리티 임포트
import { createComponentProps } from './module/adapters/componentAdapters';

// 스킨 시스템 체크를 위한 임포트
import { isSkinnableComponent } from './module/registry';

// 디바이스 뷰 모드 컨텍스트
export const DeviceViewContext = React.createContext('pc');

function ComponentRenderer({ component, components, isSelected, onSelect, viewMode = 'pc' }) {
  const dispatch = useDispatch();
  const storeComponents = useSelector(selectComponents);
  const allComponents = components || storeComponents;
  const [resizing, setResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const componentRef = useRef(null);
  
  // 이 컴포넌트의 자식 컴포넌트들 찾기
  const childComponents = allComponents.filter(comp => comp.parentId === component.id);
  
  // 부모 컴포넌트 찾기
  const parentComponent = component.parentId ? allComponents.find(comp => comp.id === component.parentId) : null;
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'COMPONENT',
    item: { 
      id: component.id, 
      type: component.type,
      componentType: component.type,
      parentId: component.parentId,
      order: component.order,
      hasChildren: childComponents.length > 0
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        dispatch(updateComponentPosition({ 
          id: component.id, 
          newPosition: component.position,
          parentId: component.parentId
        }));
      }
    }
  }), [component.id, component.type, component.parentId, component.order, childComponents.length, component.position]);
  
  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault(); // 기본 동작 방지 (링크 클릭 등으로 인한 스크롤 방지)
    

    // 현재 viewport와 scroll 위치 저장
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    const documentScrollTop = document.documentElement.scrollTop;
    const documentScrollLeft = document.documentElement.scrollLeft;
    
    // 모든 스크롤 가능한 부모 요소들의 스크롤 위치 저장
    const target = e.currentTarget;
    const scrollableParents = [];
    let element = target;
    
    while (element && element !== document.body) {
      if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
        scrollableParents.push({
          element: element,
          scrollTop: element.scrollTop,
          scrollLeft: element.scrollLeft
        });
      }
      element = element.parentElement;
    }
    

    // 만약 외부에서 onSelect prop이 전달되었다면 그것을 사용
    if (onSelect) {
      onSelect();
    } else {
      // 기존에 사용하던 dispatch 로직
      dispatch(selectComponent(component.id));
    }
    
    // 즉시 스크롤 위치 복원
    const restoreScroll = () => {
      // window 스크롤 복원
      if (window.scrollY !== scrollY || window.scrollX !== scrollX) {
        window.scrollTo(scrollX, scrollY);
      }
      
      // document 스크롤 복원
      if (document.documentElement.scrollTop !== documentScrollTop) {
        document.documentElement.scrollTop = documentScrollTop;
      }
      if (document.documentElement.scrollLeft !== documentScrollLeft) {
        document.documentElement.scrollLeft = documentScrollLeft;
      }
      
      // 부모 요소들의 스크롤 복원
      scrollableParents.forEach(parent => {
        if (parent.element.scrollTop !== parent.scrollTop) {
          parent.element.scrollTop = parent.scrollTop;
        }
        if (parent.element.scrollLeft !== parent.scrollLeft) {
          parent.element.scrollLeft = parent.scrollLeft;
        }
      });
      
    };
    
    // 동기적으로 실행
    restoreScroll();
    
    // 비동기적으로도 한 번 더 실행 (React 렌더링 후)
    requestAnimationFrame(() => {
      restoreScroll();
      
      // 두 번째 프레임에서도 확인
      requestAnimationFrame(() => {
        restoreScroll();
      });
    });
  }, [component.id, dispatch, onSelect]);
  
  // 부모 컴포넌트의 너비와 높이 구하기
  const getParentDimensions = useCallback(() => {
    if (!component.parentId) return null;
    
    const parentEl = document.getElementById(
      parentComponent?.type === COMPONENT_TYPES.ROW 
        ? `row-${component.parentId}`
        : parentComponent?.type === COMPONENT_TYPES.COLUMN
          ? `col-${component.parentId}`
          : null
    );
    
    if (!parentEl) return null;
    
    const rect = parentEl.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }, [component.parentId, parentComponent]);
  
  // 리사이즈 중 핸들러를 useCallback으로 감싸 의존성 문제 해결
  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    
    // 현재는 가로 리사이즈가 제한되어 있으므로 deltaX, newWidth, newX는 사용하지 않음
    // const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;
    // 항상 width를 100%로 설정
    const newWidth = '100%';
    let newHeight = startSize.height + deltaY;
    // x 좌표는 항상 0
    const newX = 0;
    let newY = component.position?.y || 0;
    
    // 최소 크기 제한
    newHeight = Math.max(20, newHeight);
    
    // 부모 컴포넌트 크기 제한 적용
    if (component.parentId) {
      const parentDimensions = getParentDimensions();
      if (parentDimensions) {
        // 부모 컴포넌트의 크기와 현재 컴포넌트의 위치를 고려하여 최대 크기 계산
        const maxHeight = parentDimensions.height - newY;
        
        // 부모 컴포넌트 영역을 넘지 않도록 제한
        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          // 위로 리사이즈할 때 위치 조정
          if (resizeDirection.includes('n')) {
            newY = (component.position?.y || 0) + (startSize.height - newHeight);
          }
        }
      }
    }
    
    // 높이만 업데이트
    if (resizeDirection.includes('n') || resizeDirection.includes('s')) {
      dispatch(updateComponent({
        id: component.id,
        changes: {
          size: { width: newWidth, height: newHeight },
          position: { x: newX, y: newY }
        }
      }));
    }
  }, [resizing, resizeDirection, startPos, startSize, component, dispatch, getParentDimensions]);
  
  // 리사이즈 종료 핸들러도 useCallback으로 감싸기
  const handleResizeEnd = useCallback(() => {
    setResizing(false);
    setResizeDirection(null);
    
    // 전역 이벤트 리스너 제거
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);
  
  // 리사이즈 시작 핸들러
  const handleResizeStart = useCallback((e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    
    handleSelect(e); // 컴포넌트 선택
    setResizing(true);
    setResizeDirection(direction);
    setStartPos({ x: e.clientX, y: e.clientY });
    
    // 컴포넌트 사이즈에 안전하게 접근
    const defaultSize = { width: '100%', height: 'auto' };
    const size = component.size || defaultSize;
    
    setStartSize({ 
      width: size.width || defaultSize.width, 
      height: size.height || defaultSize.height 
    });
    
    // 전역 이벤트 리스너 등록
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [component.size, handleResizeMove, handleResizeEnd, handleSelect]);
  
  // 컴포넌트 언마운트 시 이벤트 리스너 정리
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);
  
  // 컴포넌트 타입에 따라 실제 컴포넌트 렌더링 
  const renderComponent = () => {
    try {
      // 타입 정규화
      const typeStr = String(component.type).toUpperCase();
      
      // 1. 어댑터를 사용하여 컴포넌트 Props 생성
      const props = createComponentProps(component, viewMode);
      
      // 2. 모듈화된 컴포넌트 가져오기
      const ModularizedComponent = getModularizedComponent(typeStr);
      const ModularizedEditorComponent = getModularizedEditorComponent(typeStr);
      
      // 에디터 전용 props 추가
      const editorProps = {
        ...props,
        viewMode,
        isSelected,
        updateComponent: (changes) => {
          dispatch(updateComponent({ id: component.id, changes }));
        }
      };
      
      // 특별한 경우: REVIEW_FORM 컴포넌트는 아직 모듈화되지 않았으므로 기존 컴포넌트 사용
      if (typeStr === 'REVIEW_FORM' || typeStr === COMPONENT_TYPES.REVIEW_FORM) {
        return <ReviewFormComponent {...editorProps} />;
      }
      
      // 특별한 경우: TAB_LAYOUT 컴포넌트는 아직 모듈화되지 않았으므로 기존 컴포넌트 사용
      if (typeStr === 'TAB_LAYOUT' || typeStr === COMPONENT_TYPES.TAB_LAYOUT) {
        return <div>TabLayout 컴포넌트는 아직 모듈화되지 않았습니다.</div>;
      }
      
      // 3. 모듈화된 컴포넌트가 없으면 오류 표시
      if (!ModularizedComponent) {
        console.error(`모듈화된 컴포넌트를 찾을 수 없습니다: ${typeStr}`);
        return <div>모듈화된 컴포넌트를 찾을 수 없음: {typeStr}</div>;
      }
      
      // 스킨 시스템을 지원하는 컴포넌트는 우선적으로 스킨 시스템 사용
      if (isSkinnableComponent(typeStr)) {
        return <ModularizedComponent {...editorProps} />;
      }
      
      // ProductSlider는 래퍼를 통해 조건부 스킨 시스템 사용
      if (typeStr === 'PRODUCT_SLIDER') {
        return <ModularizedComponent {...editorProps} />;
      }
      
      // 4. 에디터 컴포넌트 사용 (스킨 시스템이 없는 경우만)
      if (ModularizedEditorComponent) {
        // Container 등의 레이아웃 컴포넌트인 경우 자식 처리
        if (typeStr === 'CONTAINER' || typeStr === COMPONENT_TYPES.CONTAINER) {
          return (
            <ModularizedEditorComponent {...editorProps}>
              {childComponents.length > 0 ? 
                childComponents.map(child => (
                  <ComponentRenderer 
                    key={child.id} 
                    component={child} 
                    viewMode={viewMode}
                  />
                )) 
                : props.content
              }
            </ModularizedEditorComponent>
          );
        }
        
        // Board 컴포넌트인 경우 무한 렌더링 방지를 위해 최적화된 props 사용
        if (typeStr === 'BOARD' || typeStr === COMPONENT_TYPES.BOARD) {
 
          
          // useMemo 대신 필요한 props만 포함하는 새 객체 생성
          // 중요: 어댑터에서 변환된 data 속성을 보존해야 함
          const memoizedBoardProps = {
            ...editorProps,
            // 강제 업데이트를 위한 forceUpdateCounter prop은 삭제
            // 렌더링 주기에 관련된 props는 제거하여 안정성 보장
            _optimized: true,
            _componentId: component.id
          };
          
          return <ModularizedEditorComponent {...memoizedBoardProps} />;
        }
        
        return <ModularizedEditorComponent {...editorProps} />;
      }
      
      // 5. 일반 컴포넌트 사용
      // Container 등의 레이아웃 컴포넌트인 경우 자식 처리
      if (typeStr === 'CONTAINER' || typeStr === COMPONENT_TYPES.CONTAINER) {
        return (
          <ModularizedComponent {...editorProps}>
            {childComponents.length > 0 ? 
              childComponents.map(child => (
                <ComponentRenderer 
                  key={child.id} 
                  component={child} 
                  viewMode={viewMode}
                />
              )) 
              : props.content
            }
          </ModularizedComponent>
        );
      }
      
      // Board 컴포넌트인 경우 무한 렌더링 방지를 위해 최적화된 props 사용
      if (typeStr === 'BOARD' || typeStr === COMPONENT_TYPES.BOARD) {

        
        // useMemo 대신 필요한 props만 포함하는 새 객체 생성
        // 중요: 어댑터에서 변환된 data 속성을 보존해야 함
        const memoizedBoardProps = {
          ...props, // 어댑터에서 변환된 props를 그대로 사용
          // 강제 업데이트를 위한 forceUpdateCounter prop은 삭제
          // 렌더링 주기에 관련된 props는 제거하여 안정성 보장
          _optimized: true,
          _componentId: component.id
        };
        
        return <ModularizedComponent {...memoizedBoardProps} />;
      }
      
      return <ModularizedComponent {...editorProps} />;
    } catch (error) {
      console.error('컴포넌트 렌더링 오류:', {
        type: component?.type,
        error: error.message,
        stack: error.stack,
        component: component
      });
      return (
        <div style={{ 
          border: '1px solid red', 
          padding: '10px', 
          margin: '5px', 
          background: '#ffebee' 
        }}>
          <strong>컴포넌트 렌더링 오류</strong>
          <br />
          타입: {component?.type || 'Unknown'}
          <br />
          오류: {error.message}
        </div>
      );
    }
  };
  
  // 컴포넌트 스타일 계산
  const getComponentStyles = () => {
    const isInMobile = viewMode === 'mobile';
    const isInTablet = viewMode === 'tablet';
    
    // 디바이스 타입 결정 (태블릿과 모바일은 mobile로 통합)
    const deviceType = isInMobile || isInTablet ? 'mobile' : 'pc';
    
    // 기본 스타일
    let baseStyles = {
      position: 'relative',
      width: component.size?.width || '100%',
      height: useAutoHeight ? 'auto' : (component.size?.height || 'auto'),
      minHeight: useAutoHeight ? '200px' : undefined,
      opacity: isDragging ? 0.5 : 1,
      border: 'none', // 고정된 border로 레이아웃 시프트 방지
      boxSizing: 'border-box',
      overflow: 'visible',
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingTop: component.style?.paddingTop || '0px',
      paddingBottom: component.style?.paddingBottom || '0px',
      transition: 'padding 0.2s ease',
      // 컴포넌트 스타일 병합
      ...(component.style || {})
    };
    
    // 디바이스별 속성 적용 - PC일 때는 PC 설정만 적용, 모바일/태블릿일 때는 mobile 설정만 적용
    if (component.style?.deviceProperty) {
      if (deviceType === 'pc' && component.style.deviceProperty['pc']) {
        // PC 모드일 때는 PC 설정만 적용
        baseStyles = {
          ...baseStyles,
          ...component.style.deviceProperty['pc']
        };
      } else if (deviceType === 'mobile' && component.style.deviceProperty['mobile']) {
        // 모바일/태블릿 모드일 때는 모바일 설정만 적용
        baseStyles = {
          ...baseStyles,
          ...component.style.deviceProperty['mobile']
        };
      }
    }
    
    // 선택된 컴포넌트 스타일 - CSS 클래스로만 처리하여 레이아웃 시프트 방지
    // outline과 zIndex는 CSS 클래스에서 처리하므로 여기서는 제거
    
    return baseStyles;
  };
  
  // 그리드 내부 자식 컴포넌트의 스타일 조정
  let componentStyle;
  
  // 자동 높이 사용 여부 확인
  const useAutoHeight = true; // 이제 모든 컴포넌트가 모듈화되어 자동 높이 사용
  
  // 일반 최상위 컴포넌트 스타일 설정
  componentStyle = getComponentStyles();
  
  // 컴포넌트 클래스 이름에 디바이스 모드 추가
  const componentClassName = `placed-component ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${viewMode === 'mobile' ? 'mobile-view' : viewMode === 'tablet' ? 'tablet-view' : 'desktop-view'}`;
  
  return (
    <DeviceViewContext.Provider value={viewMode}>
      <div
        id={`component-${component.id}`}
        ref={(node) => {
          drag(node);
          componentRef.current = node;
        }}
        onClick={handleSelect}
        className={componentClassName}
        style={componentStyle}
        data-device={viewMode}
      >
        {renderComponent()}
        
        {isSelected && (
          <>
            {/* 리사이즈 핸들 */}
            <div className="resize-handle n" onMouseDown={(e) => handleResizeStart(e, 'n')} style={{ top: '-5px', left: '50%', transform: 'translateX(-50%)' }} />
            <div className="resize-handle e" onMouseDown={(e) => handleResizeStart(e, 'e')} style={{ top: '50%', right: '-5px', transform: 'translateY(-50%)' }} />
            <div className="resize-handle s" onMouseDown={(e) => handleResizeStart(e, 's')} style={{ bottom: '-5px', left: '50%', transform: 'translateX(-50%)' }} />
            <div className="resize-handle w" onMouseDown={(e) => handleResizeStart(e, 'w')} style={{ top: '50%', left: '-5px', transform: 'translateY(-50%)' }} />
            
            <div className="resize-handle ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} style={{ top: '-5px', right: '-5px' }} />
            <div className="resize-handle se" onMouseDown={(e) => handleResizeStart(e, 'se')} style={{ bottom: '-5px', right: '-5px' }} />
            <div className="resize-handle sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} style={{ bottom: '-5px', left: '-5px' }} />
            <div className="resize-handle nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} style={{ top: '-5px', left: '-5px' }} />
          </>
        )}
      </div>
    </DeviceViewContext.Provider>
  );
}

// React.memo로 불필요한 리렌더링 방지
export default React.memo(ComponentRenderer, (prevProps, nextProps) => {
  // isSelected가 변경되었을 때만 리렌더링
  if (prevProps.isSelected !== nextProps.isSelected) {
    return false; // 리렌더링 필요
  }
  
  // component의 내용이 변경되었을 때만 리렌더링
  if (JSON.stringify(prevProps.component) !== JSON.stringify(nextProps.component)) {
    return false; // 리렌더링 필요
  }
  
  // viewMode가 변경되었을 때만 리렌더링
  if (prevProps.viewMode !== nextProps.viewMode) {
    return false; // 리렌더링 필요
  }
  
  // 그 외의 경우는 리렌더링하지 않음
  return true; // 리렌더링 불필요
});

